import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { ShoppingCartIcon, Bars3Icon, XMarkIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();

  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <nav className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2 text-2xl font-black text-primary tracking-tight">
            <span className="bg-primary text-white px-2 py-0.5 rounded-lg text-xl font-extrabold">G</span>
            <span>GadgetStore</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-gray-600 hover:text-primary font-medium text-sm transition-colors">
              Home
            </Link>
            <Link to="/products" className="text-gray-600 hover:text-primary font-medium text-sm transition-colors">
              Products
            </Link>

            {user && (
              <Link to="/my-orders" className="text-gray-600 hover:text-primary font-medium text-sm transition-colors">
                My Orders
              </Link>
            )}

            {user?.role === 'admin' && (
              <Link
                to="/admin/dashboard"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 rounded-lg text-xs font-semibold transition-colors"
              >
                👑 Admin Panel
              </Link>
            )}

            {/* Shopping Cart Button */}
            <button
              onClick={() => navigate('/cart')}
              className="relative p-2 text-gray-600 hover:text-primary transition-colors rounded-full hover:bg-gray-50"
              title="Shopping Cart"
            >
              <ShoppingCartIcon className="h-6 w-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Auth section */}
            {user ? (
              <div className="flex items-center gap-3 border-l pl-4">
                <div className="text-right">
                  <div className="text-xs font-bold text-gray-800">{user.name}</div>
                  <div className="text-[10px] text-gray-500 capitalize">{user.role === 'admin' ? '👑 Admin' : 'Customer'}</div>
                </div>
                <button
                  onClick={logout}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Sign Out"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 border-l pl-4">
                <Link to="/login" className="text-gray-700 hover:text-primary font-medium text-sm">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary text-xs py-2 px-4 shadow-sm">
                  Register
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center space-x-3 md:hidden">
            <button
              onClick={() => navigate('/cart')}
              className="relative p-2 text-gray-600"
            >
              <ShoppingCartIcon className="h-6 w-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              className="p-2 text-gray-600"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown */}
        {isOpen && (
          <div className="md:hidden py-4 border-t space-y-3">
            <Link to="/" className="block py-2 text-gray-700 font-medium" onClick={() => setIsOpen(false)}>
              Home
            </Link>
            <Link to="/products" className="block py-2 text-gray-700 font-medium" onClick={() => setIsOpen(false)}>
              Products
            </Link>

            {user && (
              <Link to="/my-orders" className="block py-2 text-gray-700 font-medium" onClick={() => setIsOpen(false)}>
                My Orders
              </Link>
            )}

            {user?.role === 'admin' && (
              <Link
                to="/admin/dashboard"
                className="block py-2 text-purple-700 font-bold"
                onClick={() => setIsOpen(false)}
              >
                👑 Admin Dashboard
              </Link>
            )}

            {user ? (
              <div className="pt-3 border-t flex justify-between items-center">
                <div>
                  <p className="font-bold text-sm text-gray-800">{user.name}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
                <button
                  onClick={() => {
                    logout();
                    setIsOpen(false);
                  }}
                  className="btn-secondary text-xs text-red-600"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="pt-3 border-t flex gap-3">
                <Link
                  to="/login"
                  className="btn-secondary text-xs flex-1 text-center"
                  onClick={() => setIsOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="btn-primary text-xs flex-1 text-center"
                  onClick={() => setIsOpen(false)}
                >
                  Register
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;